import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Users } from 'lucide-react';
import { AdminNotification } from '../../types';
import { fetchUnreadNotifications, markNotificationsRead } from '../../services/notificationService';

interface AdminNotificationBellProps {
  variant?: 'onPrimary' | 'dark';
}

const POLL_INTERVAL_MS = 30000;

const AdminNotificationBell: React.FC<AdminNotificationBellProps> = ({ variant = 'onPrimary' }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [displayedItems, setDisplayedItems] = useState<AdminNotification[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const loadUnread = useCallback(async () => {
    try {
      const items = await fetchUnreadNotifications();
      if (!openRef.current) {
        setUnreadCount(items.length);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    loadUnread();
    const interval = window.setInterval(loadUnread, POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [loadUnread]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = async () => {
    if (open) {
      setOpen(false);
      return;
    }

    try {
      const items = await fetchUnreadNotifications();
      setDisplayedItems(items);
      setOpen(true);
      setUnreadCount(0);
      if (items.length > 0) {
        await markNotificationsRead();
      }
    } catch (error) {
      console.error('Failed to open notifications:', error);
    }
  };

  const handleItemClick = (notification: AdminNotification) => {
    setOpen(false);
    if (notification.type === 'registration') {
      navigate('/admin/registrations');
      return;
    }
    navigate('/admin/custom-interests' + (notification.customTourId ? `?tourId=${notification.customTourId}` : ''));
  };

  const isOnPrimary = variant === 'onPrimary';
  const bellButtonClass = isOnPrimary
    ? 'relative inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-white text-white transition-all duration-300 hover:bg-white hover:text-[var(--primary-deep-blue)]'
    : 'relative inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-[var(--primary-deep-blue)] text-[var(--primary-deep-blue)] transition-all duration-300 hover:bg-[var(--primary-deep-blue)] hover:text-white';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleBellClick}
        className={bellButtonClass}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-[var(--primary-gold)] text-white text-xs font-semibold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-[min(22rem,calc(100vw-2rem))] bg-white rounded-2xl shadow-2xl border border-[var(--neutral-medium-gray)] z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--neutral-medium-gray)]">
            <h3 className="font-[var(--font-secondary)] font-semibold text-[var(--neutral-charcoal)]">
              Notifications
            </h3>
          </div>
          {displayedItems.length === 0 ? (
            <p className="px-4 py-8 text-center body-small text-[var(--secondary-slate)]">
              No new notifications
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {displayedItems.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => handleItemClick(notification)}
                    className="w-full text-left px-4 py-3 hover:bg-[var(--secondary-pearl)] transition-colors border-b border-[var(--neutral-light-gray)] last:border-b-0"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-[var(--primary-deep-blue)]">
                        {notification.type === 'registration' ? <Users size={16} /> : <FileText size={16} />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--primary-gold)]">
                          {notification.type === 'registration' ? 'Registration' : 'Custom tour'}
                        </p>
                        <p className="text-sm font-semibold text-[var(--neutral-charcoal)] mt-0.5">
                          {notification.title}
                        </p>
                        <p className="text-sm text-[var(--secondary-slate)] mt-1 break-words">
                          {notification.message}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotificationBell;
